// Post-login deep-link preservation (TICKET-signin-onboarding-edge-cases #3). When a logged-out user
// hits a protected route we stash the intended path, then restore it after they authenticate and land
// back on `/`. The value round-trips through sessionStorage (survives the Auth0 redirect in the same
// tab), so no Auth0 appState wiring is needed.
// CONSTRAINT (foundation-security — no open redirects): only ever restore a same-origin, internal
// absolute path. Absolute URLs, protocol-relative (`//host`), and backslash tricks fall back to /home.

const RETURN_TO_KEY = 'BH:returnTo';
const DEFAULT_ROUTE = '/home';

/**
 * Reduce an arbitrary stored value to a safe internal route. Rejects anything that isn't a plain
 * internal absolute path, and refuses to bounce back to the public entry (`/`) which would loop.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_ROUTE;
  // Must be an internal absolute path — not `//evil.com`, `http(s)://…`, or `/\evil.com`.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return DEFAULT_ROUTE;
  // Restoring `/` (the public landing) would defeat the redirect / loop.
  if (raw === '/') return DEFAULT_ROUTE;
  return raw;
}

/** Remember where an unauthenticated visitor was trying to go, before redirecting them to sign in. */
export function stashReturnTo(path: string): void {
  try {
    if (path && path !== '/') sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // sessionStorage can throw in privacy modes — deep-linking is best-effort, never fatal.
  }
}

/** Read (without clearing) the stashed intended route, sanitized. Pair with {@link clearReturnTo}. */
export function readReturnTo(): string {
  try {
    return sanitizeReturnTo(sessionStorage.getItem(RETURN_TO_KEY));
  } catch {
    return DEFAULT_ROUTE;
  }
}

/** Drop the stashed route once it has been consumed. */
export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // ignore
  }
}
